/**
 * Причины, по которым карточка не уедет или уедет неполной.
 *
 * Красное — блокировки: публикация будет отклонена. Жёлтое — пробелы: карточка
 * уедет, но поле на площадке останется пустым. В прежней вкладке всё это было
 * одной формулировкой «ревизия неполна», по которой нельзя было понять, что
 * делать.
 */

import React from 'react'
import { AlertTriangle } from 'lucide-react'
import type { MarketplaceCardDto } from '../../../pipeline/api'
import { blockingReason, gapReason, marketplaceErrorHint } from './marketplaceReasons'
import { blockingCodes, cardState, gapCodes } from './marketplaceCardState'

const BOX =
  'flex items-start gap-2 rounded-lg border px-3 py-2 text-xs leading-relaxed'
const RED =
  'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
const AMBER =
  'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200'

const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-mono text-2xs opacity-70">{children}</span>
)

export const MarketplaceReasons: React.FC<{ card: MarketplaceCardDto }> = ({ card }) => {
  const state = cardState(card)
  const blocking = blockingCodes(card)
  const gaps = gapCodes(card)
  const errorHint = marketplaceErrorHint(card.error)

  const blocks: React.ReactNode[] = []

  if (state === 'error') {
    blocks.push(
      <div key="error" role="alert" className={`${BOX} ${RED}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">Площадка отклонила карточку</b>
          {errorHint}
          {card.error && (
            <div className="mt-1">
              <Code>{card.error}</Code>
            </div>
          )}
        </div>
      </div>,
    )
  }

  if (state === 'no_revision') {
    blocks.push(
      <div key="no-revision" role="alert" className={`${BOX} ${RED}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">Не найден замороженный состав карточки</b>
          Проверять и одобрять нечего: публикация всегда ссылается на конкретную ревизию. Если
          генерация ещё идёт — дождитесь её и обновите страницу; если остановилась с ошибкой —
          нажмите «Повторить шаг».
        </div>
      </div>,
    )
  }

  if (state === 'live_no_photo') {
    blocks.push(
      <div key="no-photo" className={`${BOX} ${AMBER}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">На витрине без фото</b>
          Карточка заведена, но изображения из кабинета не подтянулись — покупатель видит пустое
          место.
        </div>
      </div>,
    )
  }

  if (state === 'commercial_pim') {
    blocks.push(
      <div key="commercial" className={`${BOX} ${AMBER}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">Цена и остаток сохранены только в PIM</b>
          Отправка коммерческих значений на площадку не подключена, внешнее состояние карточки не
          подтверждено.
        </div>
      </div>,
    )
  }

  if (blocking.length > 0) {
    blocks.push(
      <div key="blocking" role="alert" className={`${BOX} ${RED}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">Публикация не пойдёт</b>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {blocking.map((code) => {
              const reason = blockingReason(code)
              return (
                <li key={code}>
                  {reason.title}
                  {reason.detail && ` — ${reason.detail}`} <Code>{code}</Code>
                </li>
              )
            })}
          </ul>
        </div>
      </div>,
    )
  }

  if (gaps.length > 0) {
    blocks.push(
      <div key="gaps" className={`${BOX} ${AMBER}`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <div>
          <b className="block">Уедет, но поля останутся пустыми</b>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {gaps.map((code) => {
              const reason = gapReason(code)
              return (
                <li key={code}>
                  {reason.title}
                  {reason.detail && ` — ${reason.detail}`} <Code>{code}</Code>
                </li>
              )
            })}
          </ul>
        </div>
      </div>,
    )
  }

  if (blocks.length === 0) return null
  return <div className="space-y-2">{blocks}</div>
}

export default MarketplaceReasons
